import { z } from 'zod';
/**
 * Model providers the platform can use for its agents. Platform admins manage them in the web app; the
 * core stores the API keys encrypted and sends the default provider to the agent service with each call.
 */
export const PROVIDER_KINDS = ['together', 'openai', 'anthropic', 'google', 'openai_compatible'];
export const ProviderKind = z.enum(PROVIDER_KINDS);
export const Hosted = z.enum(['external', 'self_hosted']);
export const StructuredOutput = z.enum(['function_calling', 'json_schema']);
export const PROVIDER_CATALOG = {
    together: {
        label: 'Together AI',
        defaultBaseUrl: 'https://api.together.xyz/v1',
        keyRequired: true,
        thinkingSwitch: true,
        defaultHosted: 'external',
        modelPlaceholder: 'MiniMaxAI/MiniMax-M3',
        keyPlaceholder: 'tgp_v1_…',
        docsUrl: 'https://docs.together.ai/docs/serverless-models',
    },
    openai: {
        label: 'OpenAI',
        defaultBaseUrl: 'https://api.openai.com/v1',
        keyRequired: true,
        thinkingSwitch: false,
        defaultHosted: 'external',
        modelPlaceholder: 'Load models to pick one',
        keyPlaceholder: 'sk-…',
        docsUrl: 'https://platform.openai.com/docs/models',
    },
    anthropic: {
        label: 'Anthropic (Claude)',
        defaultBaseUrl: 'https://api.anthropic.com',
        keyRequired: true,
        thinkingSwitch: false,
        defaultHosted: 'external',
        modelPlaceholder: 'claude-sonnet-5-5',
        keyPlaceholder: 'sk-ant-…',
        docsUrl: 'https://docs.anthropic.com/en/docs/about-claude/models',
    },
    google: {
        label: 'Google AI (Gemini)',
        defaultBaseUrl: 'https://generativelanguage.googleapis.com',
        keyRequired: true,
        thinkingSwitch: true,
        defaultHosted: 'external',
        modelPlaceholder: 'Load models to pick one',
        keyPlaceholder: 'AIza…',
        docsUrl: 'https://ai.google.dev/gemini-api/docs/models',
    },
    openai_compatible: {
        label: 'OpenAI-compatible server',
        defaultBaseUrl: null,
        keyRequired: false,
        thinkingSwitch: false,
        defaultHosted: 'self_hosted',
        modelPlaceholder: 'The model name your server serves',
        keyPlaceholder: 'Optional',
        docsUrl: null,
    },
};
const Name = z.string().trim().min(1, 'Give the provider a name').max(80);
const ModelName = z.string().trim().min(1, 'Choose a model').max(200);
const BaseUrl = z.string().trim().url('Enter a full URL, e.g. https://…').max(500);
const ApiKey = z.string().trim().min(8, 'That key looks too short').max(500);
const Price = z.number().min(0).max(1000);
/** Rules that depend on several fields. The web form, the core and (for the URL) the agent service apply them. */
export function providerIssues(p) {
    const info = PROVIDER_CATALOG[p.kind];
    const issues = [];
    if (!info.defaultBaseUrl && !p.base_url)
        issues.push({ path: 'base_url', message: 'A self-hosted or compatible server needs its base URL' });
    if (p.base_url && p.kind !== 'openai_compatible' && !p.base_url.startsWith('https://'))
        issues.push({ path: 'base_url', message: 'Cloud providers must use https' });
    if (info.keyRequired && !p.has_key)
        issues.push({ path: 'api_key', message: `${info.label} needs an API key` });
    if (p.is_default && !p.enabled)
        issues.push({ path: 'enabled', message: 'The default provider must be enabled' });
    return issues;
}
export const CreateProvider = z.strictObject({
    name: Name,
    kind: ProviderKind,
    base_url: BaseUrl.nullable().default(null),
    model: ModelName,
    api_key: ApiKey.optional(),
    /** Defaults to the kind's usual hosting (cloud APIs external, compatible servers self-hosted). */
    hosted: Hosted.optional(),
    reasoning: z.boolean().nullable().default(null),
    structured_output: StructuredOutput.default('function_calling'),
    price_input_per_mtok: Price.default(0),
    price_output_per_mtok: Price.default(0),
    enabled: z.boolean().default(true),
    is_default: z.boolean().default(false),
});
/** Kind can't change: a different vendor is a new provider. api_key: omit to keep, null to remove. */
export const UpdateProvider = z.strictObject({
    name: Name.optional(),
    base_url: BaseUrl.nullable().optional(),
    model: ModelName.optional(),
    api_key: ApiKey.nullable().optional(),
    hosted: Hosted.optional(),
    reasoning: z.boolean().nullable().optional(),
    structured_output: StructuredOutput.optional(),
    price_input_per_mtok: Price.optional(),
    price_output_per_mtok: Price.optional(),
    enabled: z.boolean().optional(),
    is_default: z.boolean().optional(),
    revision: z.number().int().positive(),
});
/** Unsaved settings from the form, for "Test connection" and "Load models". With provider_id and no
 * api_key, the stored key is used, so an admin can test an edit without typing the key again. */
export const DraftProvider = z.strictObject({
    provider_id: z.string().uuid().optional(),
    kind: ProviderKind,
    base_url: BaseUrl.nullable().default(null),
    model: z.string().trim().max(200).default(''),
    api_key: ApiKey.optional(),
    reasoning: z.boolean().nullable().default(null),
    structured_output: StructuredOutput.default('function_calling'),
});
export const CHECK_STATUSES = [
    'connected',
    'auth_failed',
    'not_found',
    'rate_limited',
    'unreachable',
    'timeout',
    'no_structured_output',
    'invalid_config',
    'error',
];
export const CheckStatus = z.enum(CHECK_STATUSES);
/** POST /v1/providers/check on the agent service. */
export const ProviderCheckResult = z.object({
    ok: z.boolean(),
    status: CheckStatus,
    message: z.string(),
    model: z.string(),
    latency_ms: z.number().int().nonnegative(),
});
/** POST /v1/providers/models on the agent service. */
export const ModelListResult = z.object({
    ok: z.boolean(),
    status: CheckStatus,
    message: z.string(),
    models: z.array(z.string()),
});
/** The provider as the agent service receives it. Mirrors ProviderSpec in services/agents/app/providers. */
export const AgentProviderSpec = z.strictObject({
    kind: ProviderKind,
    model: z.string().min(1).max(200),
    base_url: z.string().nullable().default(null),
    api_key: z.string().nullable().default(null),
    reasoning: z.boolean().nullable().default(null),
    structured_output: StructuredOutput.default('function_calling'),
    price_input_per_mtok: z.number().min(0).default(0),
    price_output_per_mtok: z.number().min(0).default(0),
});
/** "tgp_…VElY": enough to tell keys apart, not enough to use one. */
export function keyHint(key) {
    const k = key.trim();
    return k.length >= 12 ? `${k.slice(0, 4)}…${k.slice(-4)}` : `••••${k.slice(-2)}`;
}
//# sourceMappingURL=llm-providers.js.map